import React from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import { AppContext, appContext, Teacher, useAppContext } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Button } from '../../components/simple/button'
import { BlueInput, TextInput } from '../../components/form/textInput'
import { Divider } from '../../components/simple/divider'
import { Toasts } from '../../providers/toast'

const AbbreviationUpdateRow: React.FC<{
  name: string
  startingValue: string
  defaultValue: string
  id: number | 'district'
  type: 'district' | 'school'
  reloadUser: AppContext['reloadUser']
}> = ({ name, defaultValue, id, type, reloadUser, startingValue }) => {
  const [value, setValue] = React.useState(startingValue)
  const [loading, setLoading] = React.useState(false)

  const handleUpdate = () => {
    setLoading(true)
    makeRequest<void>('/setAbbreviation', {
      abbreviation: value,
      id,
      type,
    })
      .then(async () => {
        Toasts.showToast({ message: 'Abbreviation changed' })
        await reloadUser()
        setValue('')
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <div className={`flex items-center gap-2 ${loading ? 'pointer-events-none opacity-50' : ''}`}>
      <b>{name}</b>
      <TextInput className={`${BlueInput} flex-grow`} value={value} onChange={setValue} placeholder={defaultValue} />
      <Button logo disabled={loading || value === startingValue || value.trim() === ''} onClick={handleUpdate}>
        Update
      </Button>
    </div>
  )
}

export const AbbreviationConfigModal: React.FC<{ user: Teacher; onClose: () => void }> = ({ user, onClose }) => {
  const { reloadUser } = useAppContext()
  const schoolsToShow = user.districtAccess ? user.district.schools : user.schools

  return (
    <ControlledModal
      size="md"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={true}
      title="School and District Abbreviation"
    >
      <div className="mb-3">
        Update the abbreviation that is used to refer to schools and districts in text messages.
      </div>
      {user.districtAccess && user.admin > 0 && (
        <>
          <AbbreviationUpdateRow
            name={user.district.name}
            startingValue={user.district.abbreviation || ''}
            defaultValue={user.district.defaultAbbreviation}
            id={user.district.id}
            type="district"
            reloadUser={reloadUser}
          />
          <Divider>SCHOOLS</Divider>
        </>
      )}
      {schoolsToShow.map((school) => (
        <AbbreviationUpdateRow
          key={school.id}
          name={school.name}
          startingValue={school.abbreviation || ''}
          defaultValue={school.defaultAbbreviation}
          id={school.id}
          type="school"
          reloadUser={reloadUser}
        />
      ))}
    </ControlledModal>
  )
}
